'use client';
import { useState } from 'react';

// Collapsible legend that lives above the holdings table on /portfolio
// Overview. Default-collapsed, click to expand to the full tier+axis matrix.
// Right side hosts quick-action position buttons (+ add / − remove) that drop
// the user into the inline edit flow on the holdings table below.

interface Props {
  /** Click handler for the "+ ADD POSITION" button. Typically wired to startEdit. */
  onAddPosition?: () => void;
  /** Click handler for the "− REDUCE POSITION" button. Typically wired to startEdit. */
  onReducePosition?: () => void;
  /** Click handler for the "− REMOVE POSITION" button. Typically wired to startEdit. */
  onRemovePosition?: () => void;
  /** When truthy, hide the add/remove buttons (the edit flow is already active). */
  editing?: boolean;
}

export function HoldingsReadingKey({ onAddPosition, onReducePosition, onRemovePosition, editing }: Props = {}) {
  const [open, setOpen] = useState(false);
  const showActions = !editing && (onAddPosition || onReducePosition || onRemovePosition);
  return (
    <div className="mb-3 rounded-lg border border-[#1e293b] bg-[#0d1117]/50 px-4 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-baseline gap-3 flex-wrap text-left flex-1 min-w-0"
        >
          <span className="text-[10px] uppercase tracking-wider text-[#94a3b8] font-semibold">How to read</span>
          <div className="flex items-baseline gap-2 text-[10px]">
            <Pill label="STRONG BUY" hex="#4ade80" hint="C ≥ 3.0" />
            <Pill label="BUY"        hex="#86efac" hint="1.5 – 3.0" />
            <Pill label="HOLD"       hex="#fcd34d" hint="0 – 1.5" />
            <Pill label="SELL"       hex="#f87171" hint="< 0" />
          </div>
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          {showActions && (
            <>
              {onAddPosition && (
                <button
                  type="button"
                  onClick={onAddPosition}
                  title="Add a new ticker to the portfolio"
                  className="px-2.5 py-1 rounded border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 hover:border-emerald-400/70 uppercase tracking-wide font-semibold text-[10px]"
                >
                  + Add Position
                </button>
              )}
              {onReducePosition && (
                <button
                  type="button"
                  onClick={onReducePosition}
                  title="Trim an existing position by a percentage (freed weight flows to cash)"
                  className="px-2.5 py-1 rounded border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 hover:border-amber-400/70 uppercase tracking-wide font-semibold text-[10px]"
                >
                  − Reduce Position
                </button>
              )}
              {onRemovePosition && (
                <button
                  type="button"
                  onClick={onRemovePosition}
                  title="Remove a ticker from the portfolio"
                  className="px-2.5 py-1 rounded border border-red-400/40 text-red-300 hover:bg-red-400/10 hover:border-red-400/70 uppercase tracking-wide font-semibold text-[10px]"
                >
                  − Remove Position
                </button>
              )}
            </>
          )}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="text-[10px] text-[#64748b] uppercase tracking-wide hover:text-white"
          >
            {open ? 'hide' : 'details'}
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-[#1e293b]/60 grid md:grid-cols-2 gap-4 text-[11px] leading-relaxed text-[#cbd5e1]">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-amber-300/80 mb-1">Composite columns (drive TIER)</div>
            <div><span className="font-mono text-amber-300">C now / 1w / 1m / 3m / 6m / 1y</span> — cross-sectional composite score, where the stock sits versus the entire universe today (or N periods ago). Combines Q (quality z) and V (valuation z) via the asymmetric scaled composite (α=1.25, β=0.90, shift=0.5). <span className="text-[#64748b]">Higher = ranked better vs peers.</span></div>
            <div className="mt-1.5 text-[10px] text-[#64748b]">Composite is the sole input to the STRONG BUY / BUY / HOLD / SELL tier in the rightmost column.</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-300/80 mb-1">Self-percentile columns (informational)</div>
            <div><span className="font-mono text-emerald-300">Q% / V% / C%</span> — percentile of the stock&apos;s current value within its own multi-year history (back to 2017). <span className="text-[#64748b]">Higher = better-than-usual on this axis.</span></div>
            <div className="mt-1.5 text-[10px] text-[#64748b]">A name can be high-composite + low-C% (universe likes it but at own historical low), or vice-versa. <span className="italic">drift</span> means the value has moved &gt;4.5σ from history — comparison no longer fair due to model changes.</div>
          </div>
          <div className="md:col-span-2 text-[10px] text-[#64748b] pt-1 border-t border-[#1e293b]/40">
            Bottom <span className="text-amber-400 font-mono">PORTFOLIO</span> row = weight-averaged across holdings (skips cells where data is missing, dimmed when &lt;80% of the book has data).
          </div>
        </div>
      )}
    </div>
  );
}

function Pill({ label, hex, hint }: { label: string; hex: string; hint: string }) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span
        className="px-1.5 py-0.5 rounded text-[9px] font-semibold tracking-wide"
        style={{ backgroundColor: `${hex}26`, color: hex, border: `1px solid ${hex}66` }}
      >
        {label}
      </span>
      <span className="text-[10px] text-[#64748b] tabular-nums">{hint}</span>
    </span>
  );
}
